import { Component, Input, OnDestroy, OnInit, output, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { startWith, filter, takeUntil } from 'rxjs/operators';
import { AsyncPipe, Location, NgTemplateOutlet } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { ChangeDetectionStrategy, signal, computed } from '@angular/core';

import { MenuTopBarPlus2Component } from '../menu-top-bar-plus2/menu-top-bar-plus2.component';
import { ApiErrorParser } from '~/core/helpers/api-error-parser';
import { AuthenticationService } from '~/core/services/authentication.service';
import { CompanyService } from '~/core/services/company.service';
import { HelperPlus2Service } from '~/core/services/helper-plus2.service';
import { SnackbarService } from '~/core/services/snackbar.service';
import { ItemField } from '~/core/services/item-fields/item-fields';
import { ThemeService } from '~/core/services/theme.service';
import { AnalyticsService } from '~/core/services/analytics.service';
import { CasinoService } from '~/core/services/casino.service';
import { NavigationService, ParamsActions } from '~/core/services/navigation.service';
import { EventService } from '~/core/services/event.service';
import { CasinoCustomizationService } from '~/core/services/casino-customization.service';
import { LocaleService } from '~/core/services/locale.service';
import { PaymentProviders } from '~/core/enum/paymentProviders';
import {
  SmarticoGamificationService,
  SmarticoLevel,
  SmarticoService,
  SmarticoUserProfile,
  SmarticoUserService,
} from '~/core/services/smartico';
import { CookieService } from '~/core/services/cookie.service';
import { MemoryCleanupService } from '~/core/services/memory-cleanup.service';
import { CurrencyCasePipe } from '~/core/helpers/currency-case-pipe/currency-case-pipe';
import { UtilsService } from '~/core/helpers/utils';
import { getFirstName } from '~/core/helpers/string-utils';
import { AltenarService } from '~/core/services/altenar/altenar.service';
import { calculateUserLevel, getAvatarUrl } from '~/core/services/smartico/smartico-utils';
import { QuickDepositService } from '~/core/services/quick-deposit.service';
import { GamificationProviderService } from '~/core/services/gamification-provider.service';
import { GlobalSearchService } from '~/core/services/global-search-service.service';
import { SearchContext } from '~/core/enum/search-context.enum';
import { CrmInboxService } from '~/core/services/crm-inbox.service';
import { depositMethodEnum } from '~/core/enum/depositStatusEnum';
import { HeaderOverflowRowComponent } from '~/components/header-overflow-row/header-overflow-row.component';

@Component({
  selector: 'app-menu-top-bar-mobile',
  templateUrl: './menu-top-bar-mobile.component.html',
  styleUrls: ['./menu-top-bar-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, TranslatePipe, AsyncPipe, NgTemplateOutlet, MatButton, CurrencyCasePipe, HeaderOverflowRowComponent],
})
export class MenuTopBarMobileComponent extends MenuTopBarPlus2Component implements OnInit, OnDestroy {
  router: Router;
  translate: TranslateService;
  dialog: MatDialog;
  authentication: AuthenticationService;
  companyService: CompanyService;
  helper: HelperPlus2Service;
  route: ActivatedRoute;
  snackbarService = inject(SnackbarService);
  analyticsService = inject(AnalyticsService);
  location: Location;
  casinoService: CasinoService;
  themeService: ThemeService;
  protected navigation: NavigationService;
  eventService: EventService;
  casinoCustomizationService: CasinoCustomizationService;
  localeService: LocaleService;
  protected gamificationProviderService = inject(GamificationProviderService);
  readonly apiErrorParser = inject(ApiErrorParser);
  protected cookiesService: CookieService;
  smarticoService: SmarticoService;
  smarticoUserService: SmarticoUserService;
  altenarService: AltenarService;
  smarticoGamificationService = inject(SmarticoGamificationService);
  protected quickDepositService? = inject(QuickDepositService);
  private globalSearch = inject(GlobalSearchService);
  private crmInboxService = inject(CrmInboxService);

  readonly mobileMainItems = this.headerConfigService.mobileMainItems;
  readonly mobileSubItems = this.headerConfigService.mobileSubItems;
  readonly mobileMainItemsFlat = this.headerConfigService.mobileMainItemsFlat;
  readonly mobileSubItemsFlat = this.headerConfigService.mobileSubItemsFlat;
  readonly mobileSubPosition = this.headerConfigService.mobileSubPosition;
  readonly hasMobileSubheader = this.headerConfigService.hasMobileSubheader;

  readonly stateEvents = output();
  readonly toggleEvent = output();
  readonly toggleLoginSideMenuEvent = output();
  readonly toggleUserEvent = output();
  readonly toggleChampionship = output();
  readonly printEvent = output();
  readonly selectEvent = output();
  readonly openDeposit = output();
  readonly openGuestDeposit = output();

  @Input() isIframe = false;
  @Input() hideSecondBar = false;

  hideThemeButtonMemo = false;

  public currentLevel: SmarticoLevel | null = null;
  public progressPercentage: number = 0;
  levels: SmarticoLevel[] = [];
  notificationsCount: number = 0;

  isRegistrationIncomplete = false;
  guestDepositOpened = false;

  private _userBalanceSignal = signal<number>(0);
  private _userFirstNameSignal = signal<string>('');
  private _profileSignal = signal<SmarticoUserProfile>({});

  private _userBalanceWithBonusSignal = computed(() => {
    const balance = this._userBalanceSignal();
    const user = this.userStore.userSignal();
    let totalBalance = balance;
    totalBalance += user?.bonus_credits || 0;
    if (user?.casinoBonus && Array.isArray(user?.casinoBonus)) {
      totalBalance += user.casinoBonus.reduce((sum, bonus) => sum + (bonus.rollover?.remaining_value || 0), 0);
    }
    return totalBalance;
  });

  get userBalance() {
    return this._userBalanceSignal();
  }

  get userBalanceWithBonus() {
    return this._userBalanceWithBonusSignal();
  }

  get userFirstName() {
    return this._userFirstNameSignal();
  }

  get profile() {
    return this._profileSignal();
  }

  get smarticoDisplayName() {
    if (this.isSmarticoEnabled && this.profile?.public_username) {
      return getFirstName(this.profile.public_username);
    }
    return this.userFirstName;
  }

  updateBalanceSubscription: Subscription = Subscription.EMPTY;
  loginSubscription: Subscription = Subscription.EMPTY;
  isGamificationReady: boolean = false;

  public readonly UtilsService = UtilsService;
  public destroy$ = new Subject<void>();

  constructor() {
    super();

    this.updateUserData();
    this.isRegistrationIncomplete = this.authentication.isRegistrationIncomplete();

    this.updateBalanceSubscription = this.authentication.updateUserEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isRegistrationIncomplete = this.authentication.isRegistrationIncomplete();
      this.updateUserData();
      this.cdr.markForCheck();
    });

    this.loginSubscription = this.authentication.loginEvent.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isRegistrationIncomplete = this.authentication.isRegistrationIncomplete();
      this.updateUserData();
      this.cdr.markForCheck();
    });

    if (this.gamificationProviderService?.isAnyGamificationEnabled()) {
      if (this.gamificationProviderService?.isSmarticoGamificationEnabled()) {
        this.subscriptions.push(
          this.smarticoService
            .getState()
            .pipe(
              filter(
                (state) =>
                  state.isReady &&
                  state.isUserIdentified &&
                  this.smarticoService?.isGamificationEnabled() &&
                  this.smarticoUserService?.getIsUserGamificationEnabled(),
              ),
              takeUntil(this.destroy$),
            )
            .subscribe(() => {
              this.isSmarticoReady = true;
              this.isGamificationReady = true;
              this.cdr.markForCheck();
            }),
        );
      } else {
        this.isGamificationReady = true;
      }
    }
  }

  ngOnInit() {
    super.loginListener();

    this.subscriptions.push(
      ThemeService.lightMode$.pipe(startWith()).subscribe(() => {
        this.isLightTheme = ThemeService.isLightTheme;
      }),
    );

    this.subscriptions.push(
      this.crmInboxService.getUnreadCount().subscribe((count) => {
        this.notificationsCount = count;
      }),
    );

    this.subscriptions.push(
      this.smarticoService
        .getState()
        .pipe(filter((state) => state.isReady && this.smarticoService.isGamificationEnabled()))
        .subscribe(() => {
          this.fetchLevels();
        }),
    );

    this.subscriptions.push(
      this.smarticoUserService.getUserProfile$().subscribe((profile) => {
        this._profileSignal.set(profile);
        this.cdr.markForCheck();
      }),
    );

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const company = this.companyStore.companySignal();
      const config = this.companyStore.configSignal();
      const hasGuestPaymentMethod = (config?.payments_config ?? []).findIndex((p: any) => p.method === depositMethodEnum.GUEST_PLAYER);
      const isEnabledGuestPlayers = company?.guest_player?.enable && hasGuestPaymentMethod >= 0;
      const guestPlayerPaymentProvider = config?.payments?.deposit?.guest_player?.provider;
      this.guestDepositOpened =
        params.action === ParamsActions.GUEST_DEPOSIT &&
        isEnabledGuestPlayers &&
        (guestPlayerPaymentProvider === PaymentProviders.PAGFAST || guestPlayerPaymentProvider === PaymentProviders.PAYBROKERS_SOLUTIONS);
    });

    if (this.route.snapshot.queryParams.openLogin) {
      this.openLogin();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }

    if (this.updateBalanceSubscription && !this.updateBalanceSubscription.closed) {
      this.updateBalanceSubscription.unsubscribe();
    }
    if (this.loginSubscription && !this.loginSubscription.closed) {
      this.loginSubscription.unsubscribe();
    }

    MemoryCleanupService.clearTimer('loadingRefresh');
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateUserData() {
    this.isRegistrationIncomplete = this.authentication.isRegistrationIncomplete();
    const currentUser = this.userStore.userSignal();
    const firstName = getFirstName(currentUser?.name);
    const balance = currentUser?.credits || 0;

    this._userFirstNameSignal.set(firstName);
    this._userBalanceSignal.set(balance);
  }

  openLogin() {
    if (this.isLoggedIn) {
      this.toggleLoginSideMenuEvent.emit();
    } else {
      this.authentication.openSignInScreen(this.location);
    }
  }

  toggleMenu() {
    this.toggleEvent.emit();
  }

  toggleLoginMenu() {
    this.toggleLoginSideMenuEvent.emit();
  }

  onItemClick(item: ItemField) {
    this.itemFieldsService.openItemFieldAction(item);
  }

  openLoginDialog() {
    this.authentication.openSignInScreen(this.location);
  }

  logout() {
    this.authentication.logout();
    this.companyService.updateCurrentCompany();
    this.router.navigate(['/']);
  }

  goHome() {
    this.helper.scrollToTopSubject.next(undefined);
    this.router.navigate(['']);
  }

  get hasCasinoGameOpened() {
    return this.navigation.hasCasinoGameOpened;
  }

  openSearch() {
    this.globalSearch.open({
      initialContext: SearchContext.SPORTS,
      sportsParams: {},
    });
  }

  async fetchLevels() {
    if (this.isSmarticoReady) {
      const levels = await this.smarticoGamificationService.getLevels();
      this.levels = levels.sort((a, b) => a.required_points - b.required_points);
      this.calculateLevelProgress();
    }
  }

  calculateLevelProgress() {
    if (this.levels.length && this.profile) {
      const calculation = calculateUserLevel(this.levels, this.profile);
      this.currentLevel = calculation.currentLevel;
      this.progressPercentage = calculation.progressPercentage;
      this.cdr.markForCheck();
    }
  }

  get isSmarticoEnabled() {
    return this.isSmarticoReady;
  }

  get isGamificationEnabled() {
    return this.isGamificationReady;
  }

  protected readonly getAvatarUrl = getAvatarUrl;
}
